package org.gstechnovos.jexonplastech.sections.products.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.products.model.GlobalCatalog
import org.gstechnovos.jexonplastech.sections.products.model.Product
import org.gstechnovos.jexonplastech.sections.products.styles.CardAppearKeyframes
import org.gstechnovos.jexonplastech.sections.products.styles.ProductCardStyle
import org.gstechnovos.jexonplastech.sections.products.styles.ProductsGridContainerStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.Res
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Text

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 *
 * Image Constraints (Product Photos):
 *
 * Dimensions/Aspect Ratio: The image container is hardcoded to a fixed height of 250px
 * while the width stretches to fill the grid column (fillMaxWidth()).
 *
 * Resolution: Upload images that are at least 600x400px.
 *
 * Rendering Behavior: The images use ObjectFit.Cover, meaning the browser will crop the edges
 * to perfectly fill the 250px high box.
 *
 * Critical Tip: Ensure the product itself is perfectly centered in the original photo
 * with plenty of white space/padding around it.
 * If the product touches the edges of the original image, ObjectFit.Cover will chop off
 * the top or sides of the product.
 *
 * Text Constraints:
 * Category Label: Renders at 12px in uppercase.
 * Limit to 1–2 words to ensure it fits on a single line.
 *
 * Product Name: Renders at 20px and bold. Limit to 3–5 words (e.g., "Pro-Grip Floor Wiper").
 *
 * Longer names will wrap to a second line; if one card has 1 line and another has 3 lines,
 * the grid rows will become uneven in height.
 *
 * Model/SKU Number: Renders at 14px. Keep to a standard short string (e.g., "FW-101").
 *
 */
data class ProductItem(
    val id: String, val name: String, val category: String, val modelNumber: String, val imagePath: String
)

data class ProductsGridData(
    val products: List<ProductItem>
)

// The ONLY place defaultProductsGridData should be declared
val defaultProductsGridData = ProductsGridData(
    products = listOf(
        // Cleaning Brushes
        ProductItem("1", "Square Brush", "Cleaning Brushes", "CB-001", Res.Image.Products.CLEANING_BRUSH.SQUARE_BRUSH),
        ProductItem("2", "8-Type Brush", "Cleaning Brushes", "CB-002", Res.Image.Products.CLEANING_BRUSH.TYPE_BRUSH_8),
        ProductItem("3", "Oval Brush", "Cleaning Brushes", "CB-003", Res.Image.Products.CLEANING_BRUSH.OVAL_BRUSH),
        ProductItem("4", "Pan Brush", "Cleaning Brushes", "CB-004", Res.Image.Products.CLEANING_BRUSH.PAN_BRUSH),
        ProductItem("5", "Ground Brush", "Cleaning Brushes", "CB-005", Res.Image.Products.CLEANING_BRUSH.GROUND_BRUSH),
        ProductItem(
            "6", "Wooden Brush (6\")", "Cleaning Brushes", "CB-006", Res.Image.Products.CLEANING_BRUSH.WOODEN_BRUSH_6
        ),
        ProductItem(
            "7", "Wooden Brush (7\")", "Cleaning Brushes", "CB-007", Res.Image.Products.CLEANING_BRUSH.WOODEN_BRUSH_7
        ),

        // Toilet Brushes (Hockey Type)
        ProductItem(
            "8",
            "Medium Single Hockey",
            "Toilet Brushes",
            "TB-001",
            Res.Image.Products.TOILET_BRUSHES.MEDIUM_SINGLE_HOCKEY
        ),
        ProductItem(
            "9", "Big Single Hockey", "Toilet Brushes", "TB-002", Res.Image.Products.TOILET_BRUSHES.BIG_SINGLE_HOCKEY
        ),
        ProductItem(
            "10",
            "Small Double Hockey",
            "Toilet Brushes",
            "TB-003",
            Res.Image.Products.TOILET_BRUSHES.SMALL_DOUBLE_HOCKEY
        ),
        ProductItem(
            "11", "Big Double Hockey", "Toilet Brushes", "TB-004", Res.Image.Products.TOILET_BRUSHES.BIG_DOUBLE_HOCKEY
        ),
        ProductItem("12", "Black Hockey", "Toilet Brushes", "TB-005", Res.Image.Products.TOILET_BRUSHES.BLACK_HOCKEY),
        ProductItem("13", "Round Hockey", "Toilet Brushes", "TB-006", Res.Image.Products.TOILET_BRUSHES.ROUND_HOCKEY),
        ProductItem(
            "14", "Big Round Hockey", "Toilet Brushes", "TB-007", Res.Image.Products.TOILET_BRUSHES.BIG_ROUND_HOCKEY
        ),

        // Floor Wipers
        ProductItem("15", "12\" Floor Wiper", "Floor Wipers", "FW-012", Res.Image.Products.FLOOR_WIPERS.WIPER_12),
        ProductItem("16", "18\" Floor Wiper", "Floor Wipers", "FW-018", Res.Image.Products.FLOOR_WIPERS.WIPER_18),
        ProductItem("17", "8\" Wiper", "Floor Wipers", "FW-008", Res.Image.Products.FLOOR_WIPERS.WIPER_8),

        // Brooms & Dusters
        ProductItem("18", "Big Broom", "Brooms & Dusters", "BR-001", Res.Image.Products.BROOMS.BIG_BROOM),
        ProductItem("19", "Medium Broom", "Brooms & Dusters", "BR-002", Res.Image.Products.BROOMS.MEDIUM_BROOM),
        ProductItem("20", "Small Broom", "Brooms & Dusters", "BR-003", Res.Image.Products.BROOMS.SMALL_BROOM),
        ProductItem("21", "Round Fan Brush", "Brooms & Dusters", "FB-001", Res.Image.Products.BROOMS.ROUND_FAN_BRUSH),
        ProductItem(
            "22", "Folding V-Fan Brush", "Brooms & Dusters", "FB-002", Res.Image.Products.BROOMS.FOLDING_V_FAN_BRUSH
        ),

        // Dust Pans
        ProductItem("23", "Dust Pan", "Dust Pans", "DP-001", Res.Image.Products.DUST_PANS.DUST_PAN),
        ProductItem(
            "24", "Dust Pan with Brush", "Dust Pans", "DP-002", Res.Image.Products.DUST_PANS.DUST_PAN_WITH_BRUSH
        ),

        // Baskets & Accessories
        ProductItem(
            "25",
            "Square Vegetable Basket",
            "Baskets & Accessories",
            "VB-001",
            Res.Image.Products.BASKETS_AND_ACCESSORIES.SQUARE_VEGETABLE_BASKET
        ),
        ProductItem(
            "26",
            "Round Vegetable Basket",
            "Baskets & Accessories",
            "VB-002",
            Res.Image.Products.BASKETS_AND_ACCESSORIES.ROUND_VEGETABLE_BASKET
        ),
        ProductItem(
            "27", "Cloth Clip", "Baskets & Accessories", "CC-001", Res.Image.Products.BASKETS_AND_ACCESSORIES.CLOTH_CLIP
        ),

        // Specialized
        ProductItem(
            "28",
            "Solar Cleaning Big Brush",
            "Specialized",
            "SC-001",
            Res.Image.Products.SPECIALIZED.SOLAR_CLEANING_BRUSH
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun ProductsGridSection(
    products: List<Product> = GlobalCatalog.items,
    selectedCategory: String = "All",
    searchQuery: String = "",
    onClearFilters: () -> Unit = {} // NEW: Callback to reset search/category
) {
    val palette = ColorMode.current.toSitePalette()

    val filteredProducts = products.filter { product ->
        val matchesCategory = selectedCategory == "All" || product.category == selectedCategory
        val matchesSearch = searchQuery.isBlank() || product.name.contains(
            searchQuery, ignoreCase = true
        ) || product.modelNumber.contains(
            searchQuery, ignoreCase = true
        )
        matchesCategory && matchesSearch
    }

    Column(
        modifier = ProductsGridContainerStyle.toModifier().padding(leftRight = 5.percent).padding(topBottom = 50.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SimpleGrid(
            numColumns(1, sm = 2, md = 3, xl = 4), modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(30.px)
        ) {
            // Use forEachIndexed to create a staggered delay based on the index
            filteredProducts.forEachIndexed { index, product ->
                // key() ensures Compose tracks each card during re-filtering for smooth animations
                key(product.id) {
                    Box(
                        modifier = Modifier.fillMaxWidth().animation(
                            CardAppearKeyframes.toAnimation(
                                duration = 500.ms,
                                timingFunction = AnimationTimingFunction.EaseOut,
                                fillMode = AnimationFillMode.Both,
                                delay = (index * 50).ms // Staggered delay!
                            )
                        )
                    ) {
                        ProductCard(product)
                    }
                }
            }
        }

        // --- NEW: ENGAGING EMPTY STATE ---
        if (filteredProducts.isEmpty()) {
            Column(
                modifier = Modifier.fillMaxWidth().margin(top = 80.px, bottom = 40.px),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                SpanText(
                    text = "🔍", modifier = Modifier.fontSize(48.px).margin(bottom = 16.px)
                )
                SpanText(
                    text = "No products found",
                    modifier = Modifier.color(palette.custom.darkText).fontSize(24.px).fontWeight(FontWeight.Bold)
                        .margin(bottom = 8.px)
                )
                SpanText(
                    text = "We couldn't find anything matching \"$searchQuery\". Try adjusting your filters.",
                    modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(16.px)
                        .margin(bottom = 24.px)
                )
                Button(
                    attrs = Modifier.backgroundColor(palette.brand.primaryBlue).color(palette.custom.white)
                    .padding(leftRight = 24.px, topBottom = 12.px).borderRadius(24.px).border(0.px)
                    .fontWeight(FontWeight.Bold).cursor(Cursor.Pointer)
                    // Adds a slight scale bump on hover via inline style transition
                    .transition(Transition.of(TransitionProperty.All, duration = 300.ms))
                    .styleModifier { property("box-shadow", "0 4px 14px 0 rgba(0,118,255,0.39)") }
                    .onClick { onClearFilters() } // Instantly resets search and category
                    .toAttrs()) {
                    Text("Clear Filters")
                }
            }
        }
    }
}

@Composable
fun ProductCard(product: Product) {
    val palette = ColorMode.current.toSitePalette()
    // Track hover state for micro-interactions
    var isHovered by remember { mutableStateOf(false) }

    Link(
        path = "/products/${product.modelNumber.lowercase()}",
        modifier = Modifier.textDecorationLine(TextDecorationLine.None)
    ) {
        Column(modifier = ProductCardStyle.toModifier().onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false }
            // Optional: Lift the card slightly when hovered
            .translateY(if (isHovered) (-4).px else 0.px)
            .transition(Transition.of(TransitionProperty.All, duration = 300.ms))) {
            Box(
                modifier = Modifier.fillMaxWidth().height(250.px).backgroundColor(palette.custom.lightGray)
                    .overflow(Overflow.Hidden), // Critical for the zoom effect to stay inside the box
                contentAlignment = Alignment.Center
            ) {
                Image(
                    src = product.images.firstOrNull() ?: "",
                    alt = product.name,
                    modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Contain)
                        // Smoothly scale up the image by 10% when hovered
                        .scale(if (isHovered) 1.1f else 1f).transition(
                            Transition.of(
                                TransitionProperty.All,
                                duration = 400.ms,
                                timingFunction = AnimationTimingFunction.EaseOut
                            )
                        ).unextractable()
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth().padding(24.px), verticalArrangement = Arrangement.Center
            ) {
                SpanText(
                    text = product.category.uppercase(),
                    modifier = Modifier.color(palette.brand.primaryBlue).fontSize(12.px).fontWeight(FontWeight.Bold)
                        .margin(bottom = 8.px).letterSpacing(1.px)
                )

                SpanText(
                    text = product.name,
                    modifier = Modifier.color(palette.custom.darkText).fontSize(20.px).fontWeight(FontWeight.Bold)
                        .margin(bottom = 8.px)
                )

                SpanText(
                    text = "Model: ${product.modelNumber}",
                    modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.6f)).fontSize(14.px)
                )

                // Interactive CTA with a sliding arrow
                Box(modifier = Modifier.margin(top = 12.px)) {
                    SpanText(
                        text = "View Details →",
                        modifier = Modifier.color(palette.brand.primaryBlue).fontWeight(FontWeight.Bold)
                            // Slide the text slightly to the right on hover
                            .translateX(if (isHovered) 4.px else 0.px)
                            .transition(Transition.of(TransitionProperty.All, duration = 300.ms))
                    )
                }
            }
        }
    }
}